import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { currentUser } from '../api/auth';
import { canDrill, drillRefEquals } from '../lib/drilldown';
import type { DrillKind, DrillRef } from '../lib/drilldown';
import type { User } from '../api/types';

export interface DrilldownApi {
  stack: DrillRef[];
  top: DrillRef | null;
  depth: number;
  open: (ref: DrillRef) => void;
  back: () => void;
  close: () => void;
  can: (kind: DrillKind) => boolean;
  /** The session user, fetched once here. null until loaded, or if /api/me fails. */
  user: User | null;
}

const Ctx = createContext<DrilldownApi | null>(null);

/** Marks the history entry we push, so we only ever consume our own. */
const HISTORY_MARK = 'pacebiDrill';

export function useDrilldown(): DrilldownApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useDrilldown must be used inside <DrilldownProvider>');
  return ctx;
}

/**
 * Non-throwing variant for leaf components that are rendered in isolation —
 * DrillCell, and the table tests that mount one table without the app shell.
 * Returns null outside a provider; the caller degrades to plain text rather
 * than taking down the page over a missing affordance.
 */
export function useOptionalDrilldown(): DrilldownApi | null {
  return useContext(Ctx);
}

/**
 * Holds the drill-down stack for the whole app.
 *
 * A stack rather than a single ref so ticket -> part -> account nests in place;
 * the host renders the top and offers a back arrow below it. That is the whole
 * reason the old code had to navigate away: there was nowhere to go but the
 * router.
 *
 * The user is fetched ONCE here and shared through can(). A paginated table
 * mounts hundreds of DrillCells, and each one calling /api/me would be a
 * self-inflicted request storm.
 */
export default function DrilldownProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<DrillRef[]>([]);
  const [user, setUser] = useState<User | null>(null);
  // Mirrors stack.length for the popstate listener, which is registered once
  // and would otherwise close over a stale length.
  const depthRef = useRef(0);
  depthRef.current = stack.length;

  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);

  const open = useCallback((ref: DrillRef) => {
    setStack((prev) => {
      if (drillRefEquals(prev[prev.length - 1] ?? null, ref)) return prev;
      // Entering the stack from empty: leave a history entry behind so the
      // browser/Android back button closes the modal instead of navigating
      // away from the page being drilled from.
      if (prev.length === 0) {
        try {
          window.history.pushState({ [HISTORY_MARK]: true }, '');
        } catch {
          // Some embedded webviews refuse pushState; the stack still works,
          // back just leaves the page as it did before.
        }
      }
      return [...prev, ref];
    });
  }, []);

  const back = useCallback(() => setStack((prev) => prev.slice(0, -1)), []);
  const close = useCallback(() => setStack([]), []);

  useEffect(() => {
    // Back dismisses the WHOLE stack, not one level. We push exactly one
    // history entry (on the 0->1 transition), so popping a single level would
    // consume that entry at depth 2 and let the *next* back press leave the
    // page with a modal still open — the failure this guard exists to prevent.
    // The on-screen back arrow is the affordance that steps level by level.
    //
    // Deliberately no history.back() on a programmatic close: closing often
    // races a router navigation (AccountDetailModal's "Abrir ficha completa"
    // is a <Link onClick={onClose}>), and popping the entry there could undo
    // that navigation. The cost is one swallowed back press after closing via
    // the X — a mild annoyance, versus undoing a page change.
    const onPop = () => {
      if (depthRef.current > 0) setStack([]);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const can = useCallback((kind: DrillKind) => canDrill(user, kind), [user]);

  const value = useMemo<DrilldownApi>(() => ({
    stack,
    top: stack.length > 0 ? stack[stack.length - 1] : null,
    depth: stack.length,
    open,
    back,
    close,
    can,
    user,
  }), [stack, open, back, close, can, user]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
